/**
 * ResponsiveVerificationInput Molecule
 *
 * An enhanced version of VerificationInput with responsive sizing,
 * paste support, error states, and disabled states.
 */

import { Input } from '@/components/atoms/inputs';
import React, { ChangeEvent, useEffect, useState } from 'react';

interface ResponsiveVerificationInputProps {
  onChange?: (code: string) => void;
  onComplete?: (code: string) => void;
  disabled?: boolean;
  error?: boolean;
  className?: string;
}

const ResponsiveVerificationInput: React.FC<ResponsiveVerificationInputProps> = ({
  onChange,
  onComplete,
  disabled = false,
  error = false,
  className = '',
}) => {
  const [code, setCode] = useState<Array<string>>(Array(6).fill(''));
  const [inputsRef, setInputsRef] = useState<Array<React.RefObject<HTMLInputElement>>>([]);

  useEffect(() => {
    setInputsRef(
      Array(6)
        .fill(0)
        .map(() => React.createRef<HTMLInputElement>() as React.RefObject<HTMLInputElement>)
    );
  }, []);

  useEffect(() => {
    const areAllDigitsFilled = code.every((digit) => digit !== '');
    const codeAsString = code.join('');

    onChange?.(codeAsString);

    if (areAllDigitsFilled) {
      onComplete?.(codeAsString);
    }
  }, [code, onChange, onComplete]);

  const updateCode = (digit: string, i: number) => {
    setCode((prevCode) => [...prevCode.slice(0, i), digit, ...prevCode.slice(i + 1)]);
  };

  const focusInput = (i: number) => {
    inputsRef[i]?.current?.focus();
  };

  const handleChange = (digit: string, i: number) => {
    const firstEmptyIndex = code.findIndex((c) => c === '');
    const isDeletingMiddleInput = digit === '' && i > 0 && i < code.length - 1;
    const isLastInput = i === code.length - 1;
    const isTypingAndNextInputExists = digit !== '' && inputsRef[i + 1];

    if (firstEmptyIndex !== -1 && firstEmptyIndex < i) {
      focusInput(firstEmptyIndex);
      return;
    }

    if (/[^0-9]/.test(digit) && digit !== '') {
      return;
    }

    updateCode(digit, i);

    if (isDeletingMiddleInput && isLastInput) {
      focusInput(i - 1);
    } else if (isTypingAndNextInputExists) {
      focusInput(i + 1);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, i: number) => {
    if (e.key === 'Backspace' && code[i] === '' && i > 0) {
      updateCode('', i - 1);
      focusInput(i - 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').replace(/\D/g, '').slice(0, 6);

    if (pastedData.length > 0) {
      const newCode = Array(6).fill('');
      for (let i = 0; i < Math.min(pastedData.length, 6); i++) {
        newCode[i] = pastedData[i];
      }
      setCode(newCode);

      const nextEmptyIndex = newCode.findIndex((c) => c === '');
      if (nextEmptyIndex !== -1) {
        focusInput(nextEmptyIndex);
      } else {
        focusInput(5);
      }
    }
  };

  return (
    <div className={`flex items-center justify-center gap-2 ${className}`}>
      {inputsRef.map((inputRef, i) => (
        <Input
          key={i}
          ref={inputRef}
          wrapperClassName='w-full aspect-square max-w-12 sm:max-w-14 md:max-w-16'
          className={`h-full max-h-[60px] w-full text-center text-lg font-medium transition-all duration-200 placeholder:text-zinc-300 sm:max-h-[70px] sm:text-xl md:max-h-[80px] ${
            error
              ? 'border-red-500 bg-red-50 focus:border-red-500'
              : 'border-zinc-300 focus:border-2 focus:border-blue-500'
          } ${disabled ? 'cursor-not-allowed bg-zinc-100 text-zinc-300' : 'hover:border-zinc-400'} ${
            code[i] ? 'border-blue-300 bg-blue-50' : ''
          }`}
          type='tel'
          inputMode='numeric'
          pattern='[0-9]*'
          maxLength={1}
          value={code[i]}
          placeholder='-'
          disabled={disabled}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e.target.value, i)}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(e, i)}
          onPaste={handlePaste}
          autoComplete='one-time-code'
        />
      ))}
    </div>
  );
};

export { ResponsiveVerificationInput };
export default ResponsiveVerificationInput;
